"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DefaultOracleAPI = void 0;
const big_js_1 = __importDefault(require("big.js"));
const monetary_js_1 = require("@interlay/monetary-js");
const utils_1 = require("../utils");
const currency_1 = require("../utils/currency");
const encoding_1 = require("../utils/encoding");
class DefaultOracleAPI {
    constructor(api, wrappedCurrency) {
        this.api = api;
        this.wrappedCurrency = wrappedCurrency;
    }
    getExchangeRate(currency) {
        return __awaiter(this, void 0, void 0, function* () {
            // KBTC / IBTC have an exchange rate of one
            if ((0, utils_1.isCurrencyEqual)(currency, this.wrappedCurrency)) {
                return new monetary_js_1.ExchangeRate(currency, currency, new big_js_1.default(1));
            }
            if ((0, currency_1.isLendToken)(currency)) {
                const lendTokenId = (0, encoding_1.newCurrencyId)(this.api, currency);
                const underlyingCurrencyIdRaw = yield this.api.query.loans.underlyingAssetId(lendTokenId);
                if (underlyingCurrencyIdRaw.isNone) {
                    throw new Error(`No underlying asset found for lend token with id ${lendTokenId.asLendToken.toString()}`);
                }
                const underlyingCurrencyId = underlyingCurrencyIdRaw.unwrap();
                const rawLendToUnderlying = yield this.api.query.loans.exchangeRate(underlyingCurrencyId);
                // multiply this rate with lendtoken to get underlying amount
                const lendToUnderRate = (0, utils_1.decodeFixedPointType)(rawLendToUnderlying);
                const underlyingCurrency = yield (0, currency_1.currencyIdToMonetaryCurrency)(this.api, underlyingCurrencyIdRaw.unwrap());
                const btcToUnderRate = (yield this.convertWrappedToCurrency(new monetary_js_1.MonetaryAmount(monetary_js_1.Bitcoin, 1), underlyingCurrency)).toBig();
                const btcToLendRate = btcToUnderRate.div(lendToUnderRate);
                // final rate is normalized (base unit vs base unit), construct accordingly
                return new monetary_js_1.ExchangeRate(this.wrappedCurrency, currency, btcToLendRate);
            }
            const oracleKey = (0, utils_1.createExchangeRateOracleKey)(this.api, currency);
            const encodedRawRate = (0, utils_1.unwrapRawExchangeRate)(yield this.api.query.oracle.aggregate(oracleKey));
            if (encodedRawRate === undefined) {
                return Promise.reject(new Error(`No exchange rate for given currency: ${currency.ticker}`));
            }
            const decodedRawRate = (0, utils_1.decodeFixedPointType)(encodedRawRate);
            return new monetary_js_1.ExchangeRate(this.wrappedCurrency, currency, decodedRawRate, utils_1.ATOMIC_UNIT, utils_1.ATOMIC_UNIT);
        });
    }
    convertWrappedToCurrency(amount, currency) {
        return __awaiter(this, void 0, void 0, function* () {
            const rate = yield this.getExchangeRate(currency);
            return rate.toCounter(amount);
        });
    }
    convertCollateralToWrapped(amount) {
        return __awaiter(this, void 0, void 0, function* () {
            const rate = yield this.getExchangeRate(amount.currency);
            return rate.toBase(amount);
        });
    }
    getOnlineTimeout() {
        return __awaiter(this, void 0, void 0, function* () {
            const moment = yield this.api.query.oracle.maxDelay();
            return moment.toNumber();
        });
    }
    setExchangeRate(exchangeRate) {
        const encodedExchangeRate = (0, utils_1.encodeUnsignedFixedPoint)(this.api, exchangeRate.toBig([utils_1.ATOMIC_UNIT, utils_1.ATOMIC_UNIT]));
        const oracleKey = (0, utils_1.createExchangeRateOracleKey)(this.api, exchangeRate.counter);
        const tx = this.api.tx.oracle.feedValues([[oracleKey, encodedExchangeRate]]);
        return { extrinsic: tx, event: this.api.events.oracle.FeedValues };
    }
    getBitcoinFees() {
        return __awaiter(this, void 0, void 0, function* () {
            const oracleKey = (0, utils_1.createFeeEstimationOracleKey)(this.api);
            const fees = yield this.api.query.oracle.aggregate(oracleKey);
            const parseFees = (fee) => {
                const inner = (0, utils_1.unwrapRawExchangeRate)(fee);
                if (inner !== undefined) {
                    return (0, utils_1.decodeFixedPointType)(inner);
                }
                throw new Error("Bitcoin fee estimate not set");
            };
            return parseFees(fees);
        });
    }
    setBitcoinFees(fees) {
        if (!fees.round().eq(fees)) {
            throw new Error("tx fees must be an integer amount of satoshi");
        }
        else if (fees.lt(0)) {
            throw new Error("tx fees must be a positive amount of satoshi");
        }
        const oracleKey = (0, utils_1.createFeeEstimationOracleKey)(this.api);
        const encodedFee = (0, utils_1.encodeUnsignedFixedPoint)(this.api, fees);
        const tx = this.api.tx.oracle.feedValues([[oracleKey, encodedFee]]);
        return { extrinsic: tx, event: this.api.events.oracle.FeedValues };
    }
    getSourcesById() {
        return __awaiter(this, void 0, void 0, function* () {
            const oracles = yield this.api.query.oracle.authorizedOracles.entries();
            const nameMap = new Map();
            oracles.forEach((oracle) => nameMap.set((0, utils_1.storageKeyToNthInner)(oracle[0]).toString(), oracle[1].toUtf8()));
            return nameMap;
        });
    }
    getValidUntil(counterCurrency) {
        return __awaiter(this, void 0, void 0, function* () {
            const oracleKey = (0, utils_1.createExchangeRateOracleKey)(this.api, counterCurrency);
            const validUntil = yield this.api.query.oracle.validUntil(oracleKey);
            return validUntil.isSome ? (0, utils_1.convertMoment)(validUntil.value) : Promise.reject("No such oracle key");
        });
    }
    isOnline() {
        return __awaiter(this, void 0, void 0, function* () {
            const errors = yield this.api.query.security.errors();
            return !this.hasOracleError(Array.from(errors));
        });
    }
    getRawValuesUpdated(key) {
        return __awaiter(this, void 0, void 0, function* () {
            const isSet = yield this.api.query.oracle.rawValuesUpdated(key);
            return isSet.unwrap().isTrue;
        });
    }
    hasOracleError(errors) {
        for (const error of errors.values()) {
            if (error.isOracleOffline) {
                return true;
            }
        }
        return false;
    }
}
exports.DefaultOracleAPI = DefaultOracleAPI;
//# sourceMappingURL=oracle.js.map